import { useCallback, useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { useMsal } from '@azure/msal-react';
import { asBlob } from 'html-docx-js-typescript';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { useProviderServices } from '../../../app/providers/AppServicesContext';
import { upsertSavedPrompt } from '../../../redux/features/chatPersonalization/chatPersonalizationSlice';
import { appendMessages } from '../../../redux/features/chatMessages/chatMessagesSlice';
import { setIsResponseGenerating, setStreamingMessageId } from '../../../redux/features/chatUi/chatUiSlice';
import { upsertSession } from '../../../redux/features/chatSessions/chatSessionsSlice';
import { SendChatMessageCommand } from '../../../application/chat-messages/models/SendChatMessageCommand';
import { sendChatMessageHandler } from '../../../application/chat-messages/handlers/SendChatMessageHandler';
import { selectAgentsById } from '../../../domain/selectors/base/agents/selectAgentsById';
import { selectSelectedMessages } from '../../../domain/selectors/selected/chat/selectSelectedMessages';
import { selectSelectedSession } from '../../../domain/selectors/selected/chat/selectSelectedSession';
import { resolveSessionAgent } from '../../../domain/resolvers/resolveSessionAgent';
import { ChatProviderType } from '../../../domain/chat/models/ChatProviderType';
import { AuthorRoles, ChatMessage, UserFeedback } from '../../../domain/messages/models/ChatMessage';
import { buildAvatarSrc } from '../../utils/AvatarUtils';
import { acquireApiAccessTokenAsync, acquireNexusApiAccessTokenAsync } from '../../../services/auth/AuthService';
import { addPptxExportingMessageId, removePptxExportingMessageId } from '../../../redux/features/chatUi/chatUiSlice';
import ExportUtilsPPTX from '../../utils/ExportUtilsPPTX';
import { alerts } from '../../../shared/ui/Alerts';

const AI_DISCLAIMER_TEXT =
    'AI-generated content may be incorrect. All references or sources should be reviewed for relevance, credibility and accuracy.';

const downloadBrowserFile = (file: File): void => {
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp);
    const dateString = date.toLocaleDateString([], { month: 'numeric', day: 'numeric' });
    const timeString = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return date.toDateString() !== new Date().toDateString() ? dateString + ' ' + timeString : timeString;
};

const sanitizeFileName = (value: string): string => value.replace(/[\\/:*?"<>|]/g, '_').trim();

export interface UseChatHistoryItemInput {
    message: ChatMessage;
    messageIndex: number;
}

export interface UseChatHistoryItemVm {
    isUser: boolean;
    isBot: boolean;
    isProGPTBot: boolean;
    isLatestBotMessage: boolean;
    showInfoButton: boolean;
    showCopyButton: boolean;
    showSaveButton: boolean;
    showDownloadButton: boolean;
    showFeedbackButtons: boolean;
    showRegenerateButton: boolean;
    showDisclaimer: boolean;
    authorName: string;
    avatarSrc?: string;
    timestampText: string;
    disclaimerText: string;
    feedbackValue: UserFeedback;
    messageContentElementId: string;
    isCurrentMessageExportingToPptx: boolean;
    onSavePrompt: () => Promise<void>;
    onDownloadAsWord: () => void;
    onDownloadAsPptx: () => void;
    onRegenerate: () => void;
    onTogglePositiveFeedback: () => void;
    onToggleNegativeFeedback: () => void;
    isPending: boolean;
    isStreaming: boolean;
    pendingStatusText: string;
    pendingFadeKey: number;
}

export const useChatHistoryItem = ({ message, messageIndex }: UseChatHistoryItemInput): UseChatHistoryItemVm => {
    const dispatch = useAppDispatch();
    const providerServices = useProviderServices();
    const { instance } = useMsal();
    const session = useAppSelector(selectSelectedSession);
    const selectedMessages = useAppSelector(selectSelectedMessages);
    const agentsById = useAppSelector(selectAgentsById);
    const activeUserInfo = useAppSelector((state) => state.app.activeUserInfo);
    const selectedSessionId = useAppSelector((state) => state.chatUi.selectedSessionId);
    const pptxExportingMessageIds = useAppSelector(
        (state) => state.chatUi.bySessionId[message.chatId]?.pptxExportingMessageIds ?? [],
        shallowEqual,
    );
    const [feedbackValue, setFeedbackValue] = useState<UserFeedback>(message.userFeedback ?? UserFeedback.Unknown);
    const messageContentElementId = `temp-chat-message-${message.id ?? `${message.chatId}-${messageIndex}`}`;
    const isCurrentMessageExportingToPptx = pptxExportingMessageIds.includes(message.id ?? '');

    const isUser = message.authorRole === AuthorRoles.User;
    const isBot = message.authorRole === AuthorRoles.Bot;
    const isBulkStatusMessage = message.id.startsWith('bulk_status_');
    const latestBotIndex = useMemo(() => {
        for (let i = selectedMessages.length - 1; i >= 0; i -= 1) {
            if (selectedMessages[i].authorRole === AuthorRoles.Bot) {
                return i;
            }
        }

        return -1;
    }, [selectedMessages]);
    const isLatestBotMessage = isBot && messageIndex === latestBotIndex;
    const isProGPTBot = isBot && session?.providerType === ChatProviderType.ProGPT;

    const resolvedAgent = session ? resolveSessionAgent(session, agentsById) : null;
    const authorName = isUser
        ? (activeUserInfo?.username ?? message.userName ?? 'User')
        : (resolvedAgent?.name ?? message.userName ?? 'Assistant');

    const avatarSrc = isUser
        ? activeUserInfo?.photoUrl
        : session
          ? buildAvatarSrc(session.providerType, resolvedAgent?.agentIconUrl)
          : undefined;

    const onSavePrompt = useCallback(async (): Promise<void> => {
        if (!session) {
            alerts.error('Please select a supported chat before saving a prompt.');
            return;
        }

        const trimmedValue = message.content.trim();
        if (!trimmedValue) {
            alerts.error('Prompt text cannot be empty.');
            return;
        }

        try {
            const accessToken = await acquireApiAccessTokenAsync({
                msalInstance: instance,
            });

            const savedPrompt = await providerServices.proGPT.personalization.createPromptAsync(
                {
                    text: trimmedValue,
                    agentId: session.agentId,
                },
                accessToken,
            );

            dispatch(upsertSavedPrompt(savedPrompt));
            alerts.success('Added prompt to your library.');
        } catch {
            alerts.error('Failed to save prompt.');
        }
    }, [dispatch, instance, message.content, providerServices, session]);

    const onDownloadAsWord = useCallback((): void => {
        if (!isProGPTBot) {
            return;
        }

        const downloadAsync = async (): Promise<void> => {
            const messageElement = document.getElementById(messageContentElementId);
            if (!messageElement) {
                return;
            }

            const preHtml = `
				<!DOCTYPE html>
				<html lang="en">
				<head>
					<meta charset="UTF-8" />
					<title>Document</title>
					<style>
						body { font-family: "Arial", sans-serif; font-size: 10pt; line-height: 14pt; }
						h1 { font-family: "Barlow Condensed Medium", sans-serif; font-weight: normal; color: #004068; font-size: 32pt; margin-top: 20pt; margin-bottom: 20pt; }
						h2, h3 { color: #004068; }
						p, li { margin-top: 0pt; margin-bottom: 6pt; }
						code { background-color: #f5f5f5; color: #004068; }
						table { width: 100%; border-collapse: collapse; font-size: 9pt; }
						th, td { line-height: 9pt; border: 1px solid #ffffff; text-align: center; margin: auto 0; padding: 10px; }
						th, tfoot td { font-size: 10pt; line-height: 10pt; background-color: #004068; color: #ffffff; }
						.oddRow { background-color: #E5F5F7; }
						.evenRow { background-color: #FFFFFF; }
					</style>
				</head>
				<body>`;

            const parser = new DOMParser();
            const doc = parser.parseFromString(messageElement.innerHTML, 'text/html');
            doc.querySelectorAll('table').forEach((table) => {
                table.querySelectorAll('tr').forEach((row, index) => {
                    if (index % 2 === 0) {
                        row.classList.add('evenRow');
                    } else {
                        row.classList.add('oddRow');
                    }
                });
            });

            const html = `${preHtml}${doc.body.innerHTML}</body></html>`;
            const converted = await asBlob(html, {
                orientation: 'portrait',
                margins: { top: 720, right: 720, bottom: 720, left: 720 },
            });

            const blob =
                converted instanceof Blob
                    ? converted
                    : new Blob([converted as BlobPart], {
                          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                      });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${sanitizeFileName(authorName)}-message-${messageIndex + 1}.docx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        };

        void downloadAsync();
    }, [authorName, isProGPTBot, messageContentElementId, messageIndex]);

    const onDownloadAsPptx = useCallback((): void => {
        const downloadAsync = async (): Promise<void> => {
            dispatch(addPptxExportingMessageId({ sessionId: message.chatId, messageId: message.id ?? '' }));

            try {
                const messageElement = document.getElementById(messageContentElementId);
                if (!messageElement) {
                    console.error('[ExportPPTX] messageElement not found for id:', messageContentElementId);
                    return;
                }

                const exportContentElement = messageElement.querySelector('div') ?? messageElement;

                // Find the deepest wrapper div that contains the actual content nodes (p, h1, ul, etc.)
                // The ReactMarkdown output is nested inside wrapper divs
                let contentRoot: HTMLElement = exportContentElement;
                while (
                    contentRoot.children.length === 1 &&
                    contentRoot.children[0].tagName === 'DIV' &&
                    !contentRoot.querySelector(
                        ':scope > p, :scope > h1, :scope > h2, :scope > h3, :scope > ul, :scope > ol, :scope > table',
                    )
                ) {
                    contentRoot = contentRoot.children[0] as HTMLElement;
                }
                console.log('[ExportPPTX] contentRoot childNodes:', contentRoot.childNodes.length, contentRoot.tagName);

                const exporter = new ExportUtilsPPTX({ data: contentRoot as HTMLElement });

                const htmlText = exporter.createPPTX();
                console.log('[ExportPPTX] htmlText result:', htmlText);
                if (!htmlText || htmlText.length === 0) {
                    console.warn('[ExportPPTX] createPPTX returned empty array - no slides generated');
                    return;
                }

                const accessToken = await acquireApiAccessTokenAsync({
                    msalInstance: instance,
                });

                const file = await providerServices.proGPT.messages.downloadChatMessageAsync(
                    {
                        htmlText,
                        fileName: `${authorName.replace(/\s+/g, '-')}-message-${messageIndex + 1}`,
                    },
                    accessToken,
                );

                downloadBrowserFile(file);
            } finally {
                dispatch(removePptxExportingMessageId({ sessionId: message.chatId, messageId: message.id ?? '' }));
            }
        };

        void downloadAsync();
    }, [
        authorName,
        dispatch,
        instance,
        message.chatId,
        message.id,
        messageContentElementId,
        messageIndex,
        providerServices.proGPT.messages,
    ]);

    const onRegenerate = useCallback(async (): Promise<void> => {
        if (!selectedSessionId || !selectedMessages?.length || !session) {
            return;
        }

        const latestUserMessage = [...selectedMessages].reverse().find((m) => m.authorRole === AuthorRoles.User);

        if (!latestUserMessage?.content) {
            return;
        }

        const placeholderBotMessage: ChatMessage = {
            id: `bot_${selectedSessionId}_${Date.now()}`,
            chatId: selectedSessionId,
            type: message.type,
            timestamp: Date.now(),
            userName: resolvedAgent?.name ?? 'Assistant',
            content: '',
            authorRole: AuthorRoles.Bot,
        };

        dispatch(upsertSession({ ...session, modifiedDate: Date.now() }));
        dispatch(appendMessages({ sessionId: selectedSessionId, messages: [placeholderBotMessage] }));
        dispatch(setStreamingMessageId({ sessionId: selectedSessionId, value: placeholderBotMessage.id }));
        // dispatch(setBotResponseStatus({ sessionId: selectedSessionId, value: placeholderBotMessage.id }));
        dispatch(setIsResponseGenerating({ sessionId: selectedSessionId, value: true }));

        try {
            const accessToken = session.providerType === ChatProviderType.Nexus
                ? await acquireNexusApiAccessTokenAsync({ msalInstance: instance })
                : await acquireApiAccessTokenAsync({ msalInstance: instance });

            const command: SendChatMessageCommand = {
                chatId: selectedSessionId,
                input: latestUserMessage.content,
                regenerate: true,
            };

            await sendChatMessageHandler({
                command,
                session,
                accessToken,
                providerServices,
            });
        } catch (error) {
            console.error('Failed to regenerate message', error);
            alerts.error('Failed to regenerate response. Please try again.');
            dispatch(setIsResponseGenerating({ sessionId: selectedSessionId, value: false }));
        }
    }, [dispatch, selectedMessages, selectedSessionId, session, providerServices, instance, activeUserInfo, message.type]);

    const updateFeedbackAsync = useCallback(
        async (nextFeedback: UserFeedback): Promise<void> => {
            if (!isProGPTBot || !message.id || !session) {
                return;
            }

            const feedbackToSend = feedbackValue === nextFeedback ? UserFeedback.Unknown : nextFeedback;
            setFeedbackValue(feedbackToSend);

            const accessToken = await acquireApiAccessTokenAsync({
                msalInstance: instance,
            });

            const feedbackText = UserFeedback[feedbackToSend];
            await providerServices.proGPT.messages.createChatFeedbackAsync(
                message.id,
                message.chatId,
                feedbackText,
                accessToken,
            );
        },
        [feedbackValue, instance, isProGPTBot, message.chatId, message.id, providerServices.proGPT.messages, session],
    );

    const onTogglePositiveFeedback = useCallback((): void => {
        void updateFeedbackAsync(UserFeedback.Positive);
    }, [updateFeedbackAsync]);

    const onToggleNegativeFeedback = useCallback((): void => {
        void updateFeedbackAsync(UserFeedback.Negative);
    }, [updateFeedbackAsync]);

    // --- Pending / streaming state ---
    const sessionUiState = useAppSelector((state) =>
        selectedSessionId ? state.chatUi.bySessionId[selectedSessionId] : undefined,
    );
    const isActiveStreamingMessage =
        sessionUiState?.streamingMessageId === message.id || sessionUiState?.bulkStatusMessageId === message.id;
    const isPending = isBot && !message.content && isActiveStreamingMessage;
    const isStreaming = isBot && isActiveStreamingMessage;

    const [pendingFadeKey] = useState(0);
    const pendingStatusText = sessionUiState?.botResponseStatus ?? '';

    return {
        isUser,
        isBot,
        isProGPTBot,
        isLatestBotMessage,
        showInfoButton: isBot && !isBulkStatusMessage,
        showCopyButton: isUser || isBot,
        showSaveButton: isUser,
        showDownloadButton: isProGPTBot && !isBulkStatusMessage,
        showFeedbackButtons: isProGPTBot && !isBulkStatusMessage,
        showRegenerateButton: isProGPTBot && !isBulkStatusMessage,
        showDisclaimer: isBot && !isBulkStatusMessage,
        authorName,
        avatarSrc,
        timestampText: formatTimestamp(message.timestamp),
        disclaimerText: AI_DISCLAIMER_TEXT,
        feedbackValue,
        messageContentElementId,
        isCurrentMessageExportingToPptx,
        onSavePrompt,
        onDownloadAsWord,
        onDownloadAsPptx,
        onRegenerate,
        onTogglePositiveFeedback,
        onToggleNegativeFeedback,
        isPending,
        isStreaming,
        pendingStatusText,
        pendingFadeKey,
    };
};

export default useChatHistoryItem;
